import { AssetFile } from 'contentful';
import { Button } from '../../../../components/Clickables/Buttons/Button';
import { InternalLink } from '../../../../components/Clickables/InternalLink/InternalLink';
import { AppOnlyPill } from '../../../../components/Clickables/Pill/AppOnlyPill';
import { Grid } from '../../../../components/Grid/Grid';
import { Heading } from '../../../../components/Heading/Heading';
import { Text } from '../../../../components/Text/Text';
import { trackEvent } from '../../../../helpers/analytics/trackEvent';
import { useIsTabletScreenWidthOrWider } from '../../../../helpers/hooks/useScreenWidth';
import { CMSFeaturePhonePreviewImage } from './CMSFeaturePhonePreviewImage';
import { CMSBannerImage } from '../CMSBannerImage/CMSBannerImage';

interface IProps {
  title: string;
  appOnlyPill: boolean;
  featureDescription: string;
  buttonText: string;
  phoneRight: boolean;
  backgroundTitle: string;
  backgroundDesktop?: AssetFile;
  backgroundMobile?: AssetFile;
  backgroundTablet?: AssetFile;
  phonePreviewDesktop?: AssetFile;
  phonePreviewTablet?: AssetFile;
  phonePreviewMobile?: AssetFile;
  position: number;
  currentPage: string;
  submitLink?: string;
  onSubmitLinkClick?: () => void;
}

export const CMSFeatureInDetailSection = ({
  title,
  appOnlyPill,
  featureDescription,
  buttonText,
  phoneRight,
  backgroundTitle,
  backgroundDesktop,
  backgroundMobile,
  backgroundTablet,
  phonePreviewDesktop,
  phonePreviewTablet,
  phonePreviewMobile,
  position,
  currentPage,
  submitLink,
  onSubmitLinkClick,
}: IProps) => {
  const isTabletScreenWidthOrWider = useIsTabletScreenWidthOrWider();
  const hasArtDirection = phonePreviewMobile ?? phonePreviewTablet;

  return (
    <div
      className="mx-auto grid max-w-(--breakpoint-lg) grid-cols-1 grid-rows-1 overflow-hidden sm:pb-20"
      data-testid="CMSFeatureInDetailSection"
    >
      <div className="col-start-1 row-start-1">
        <div className="min-h-[300px] w-full object-cover">
          {backgroundDesktop && (
            <CMSBannerImage
              bannerTitle={backgroundTitle}
              bannerDesktop={backgroundDesktop}
              bannerMobile={backgroundMobile}
              bannerTablet={backgroundTablet}
            />
          )}
        </div>

        <Grid className="mx-6 sm:mx-9 md:mx-5">
          <div
            // the hard coded padding top pushes text down on a small screen so it isn't hidden behind the phone preview
            className={`col-start-1 col-end-5 self-end pb-20 ${hasArtDirection ? 'pt-[18%]' : 'pt-[36%]'} sm:py-0 ${
              phoneRight
                ? 'sm:order-first sm:col-end-7 md:col-start-2'
                : 'sm:col-start-7 sm:col-end-13 md:col-end-12'
            }`}
          >
            {appOnlyPill && <AppOnlyPill className="my-3" />}
            <Heading as="h3" variant="md" className="mt-2 mb-2.5 sm:mt-6 md:mt-8">
              {title}
            </Heading>
            <Text variant="md">{featureDescription}</Text>
            {isTabletScreenWidthOrWider &&
              (submitLink ? (
                <InternalLink
                  variant="buttonPro"
                  to={submitLink}
                  className="px-8"
                  onClick={() => {
                    void trackEvent({
                      name: 'mykiss_cta_button_clicked',
                      properties: {
                        type: 'pro',
                        source: 'feature-in-detail-section',
                        position,
                        page: currentPage,
                      },
                    });
                  }}
                >
                  {buttonText}
                </InternalLink>
              ) : (
                <Button className="px-8" variant="buttonPro" onClick={onSubmitLinkClick}>
                  Continue
                </Button>
              ))}
          </div>
        </Grid>
      </div>

      <div className="pointer-events-none col-start-1 row-start-1">
        <div className="mx-9 grid h-full grid-cols-1 sm:grid-cols-12 md:mx-5">
          <div
            className={`relative mt-4 w-full justify-self-center sm:mt-8 sm:self-end md:mt-20 ${
              phoneRight
                ? 'sm:order-last sm:col-start-7 sm:col-end-13 md:col-end-12'
                : 'sm:col-start-1 sm:col-end-7 md:col-start-2'
            }`}
          >
            {phonePreviewDesktop && (
              <CMSFeaturePhonePreviewImage
                phonePreviewTitle={title}
                phonePreviewDesktop={phonePreviewDesktop}
                phonePreviewTablet={phonePreviewTablet}
                phonePreviewMobile={phonePreviewMobile}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
